export const LICENSE_RETRY_BUDGET_MS = 10_000
export const LICENSE_RETRY_DELAY_MS = 500

export function isRetryableLicenseError(data: unknown): boolean {
  const error = data as { code?: string; retryable?: boolean } | undefined
  return error?.code === 'LICENSE_CACHE_LEASE_UNAVAILABLE' && error.retryable === true
}

/** The budget starts at the first busy response; accepted work is never aborted. */
export async function withLicenseRetry<T>(
  operation: () => Promise<T>,
  options: {
    isBusy: (error: unknown) => boolean
    checkCancelled: () => void
    onExhausted: () => void
    beforeRetry?: () => Promise<void>
    now?: () => number
    wait?: (ms: number) => Promise<void>
  },
): Promise<T> {
  const now = options.now ?? Date.now
  const wait = options.wait ?? (ms => new Promise(resolve => setTimeout(resolve, ms)))
  let deadline: number | undefined
  options.checkCancelled()
  for (;;) {
    try {
      return await operation()
    } catch (error) {
      options.checkCancelled()
      if (!options.isBusy(error)) throw error
      deadline ??= now() + LICENSE_RETRY_BUDGET_MS
      const remaining = deadline - now()
      if (remaining > 0) {
        await wait(Math.min(LICENSE_RETRY_DELAY_MS, remaining))
        options.checkCancelled()
      }
      if (now() < deadline && options.beforeRetry) {
        await options.beforeRetry()
        options.checkCancelled()
      }
      if (now() >= deadline) {
        options.onExhausted()
        throw error
      }
    }
  }
}
